import {
  AUTO_SHELL_COPY,
  BUILD_TYPE_COPY,
  layoutShellForVibe,
  SECTION_COPY,
  SHELL_COPY,
  SHELL_META,
  VIBE_COPY,
  type LeadRecord,
} from '@flashpoint/site-dna';

interface Props {
  lead: LeadRecord;
}

/**
 * One-page printable summary of the lead's chosen wizard options.
 *
 * Designed to be opened in a new browser tab and printed (or saved as PDF)
 * as a leave-behind for the client after a discovery call. Black & white
 * safe, deliberately minimal styling, fits on one page when printed at
 * standard letter size.
 *
 * Reads source-of-truth strings from @flashpoint/site-dna/copy/* so the
 * printable view, the in-wizard cards, and any future email export all
 * speak the same language.
 */
export function PrintableOptionSet({ lead }: Props) {
  const { businessInfo, designTokens, layout } = lead.siteDna;
  const buildType = lead.engagement.buildType;
  const vibe = designTokens.vibe;
  const explicit = designTokens.layoutShell;
  const shell = explicit ?? layoutShellForVibe(vibe);
  const shellMetaPick = SHELL_META[shell];
  const shellCopy = explicit
    ? SHELL_COPY[explicit]
    : {
        ...AUTO_SHELL_COPY,
        name: `Auto (${shellMetaPick.number} ${shellMetaPick.name})`,
      };
  const buildTypeCopy = BUILD_TYPE_COPY[buildType];
  const vibeCopy = VIBE_COPY[vibe];
  const accent = designTokens.multiTonePalette.accent;
  const sections = layout.sectionOrder;
  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div style={{ ...pageStyle }}>
      <style>{`
        @media print {
          html, body { background: white; color: black; }
          .pos-print-hide { display: none !important; }
        }
        @page { margin: 0.75in; }
      `}</style>

      <header style={headerStyle}>
        <div>
          <div style={kickerStyle}>Discovery summary - prepared by Nick Nuzum</div>
          <h1 style={h1Style}>{businessInfo.name ?? 'Untitled lead'}</h1>
          <div style={metaStyle}>
            {today} · Site DNA v2 · {sections.length} sections
          </div>
        </div>
        <button
          className="pos-print-hide"
          type="button"
          onClick={() => window.print()}
          style={printBtnStyle}
        >
          Print
        </button>
      </header>

      <Block kicker="Build type" name={buildTypeCopy.name}>
        <Pitch text={buildTypeCopy.clientPitch} />
        <PillRow label="Best for" items={buildTypeCopy.bestFor} />
        <FeelLine items={buildTypeCopy.feel} />
      </Block>

      <Block kicker="Vibe" name={vibeCopy.name}>
        <ColorChip color={accent} label="Accent" />
        <Pitch text={vibeCopy.clientPitch} />
        <PillRow label="Best for" items={vibeCopy.bestFor} />
        <FeelLine items={vibeCopy.feel} />
      </Block>

      <Block kicker="Layout shell" name={shellCopy.name}>
        <Pitch text={shellCopy.clientPitch} />
        <PillRow label="Best for" items={shellCopy.bestFor} />
        <FeelLine items={shellCopy.feel} />
      </Block>

      <Block kicker="Sections (in order)">
        <ol style={olStyle}>
          {sections.map((s, i) => {
            const c = SECTION_COPY[s];
            return (
              <li key={`${s}-${i}`} style={sectionItemStyle}>
                <span style={olMarkerStyle}>{String(i + 1).padStart(2, '0')}</span>
                <span>
                  <strong>{c.name}</strong> - {c.oneLiner}
                </span>
              </li>
            );
          })}
        </ol>
      </Block>

      <footer style={footerStyle}>
        Project Flashpoint · Nuzum Method signature · This summary captures the choices we
        discussed - changes to any of these can still happen during build.
      </footer>
    </div>
  );
}

function Block({
  kicker,
  name,
  children,
}: {
  kicker: string;
  name?: string;
  children: React.ReactNode;
}) {
  return (
    <section style={blockStyle}>
      <div style={blockKickerStyle}>{kicker}</div>
      {name && <h2 style={h2Style}>{name}</h2>}
      {children}
    </section>
  );
}

function Pitch({ text }: { text: string }) {
  return <p style={pitchStyle}>"{text}"</p>;
}

function PillRow({ label, items }: { label: string; items: ReadonlyArray<string> }) {
  if (items.length === 0) return null;
  return (
    <div style={pillRowStyle}>
      <span style={pillLabelStyle}>{label}</span>
      {items.map((p) => (
        <span key={p} style={pillStyle}>
          {p}
        </span>
      ))}
    </div>
  );
}

function FeelLine({ items }: { items: ReadonlyArray<string> }) {
  if (items.length === 0) return null;
  return <div style={feelStyle}>feel · {items.join(' · ')}</div>;
}

function ColorChip({ color, label }: { color: string; label: string }) {
  return (
    <div style={colorChipRowStyle}>
      <span
        style={{
          display: 'inline-block',
          width: 20,
          height: 20,
          borderRadius: 4,
          background: color,
          border: '1px solid #ccc',
          verticalAlign: 'middle',
        }}
      />
      <span style={{ fontSize: 11, fontFamily: 'monospace', color: '#444' }}>
        {label} · {color}
      </span>
    </div>
  );
}

const pageStyle: React.CSSProperties = {
  fontFamily:
    'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  color: '#111',
  background: 'white',
  padding: '32px 40px',
  maxWidth: 720,
  margin: '0 auto',
  lineHeight: 1.5,
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  borderBottom: '1px solid #ddd',
  paddingBottom: 16,
  marginBottom: 24,
};

const kickerStyle: React.CSSProperties = {
  fontFamily: 'monospace',
  fontSize: 10,
  textTransform: 'uppercase',
  letterSpacing: '0.18em',
  color: '#777',
};

const h1Style: React.CSSProperties = {
  margin: '4px 0 0',
  fontSize: 28,
  fontWeight: 700,
  letterSpacing: '-0.01em',
};

const metaStyle: React.CSSProperties = {
  marginTop: 4,
  fontSize: 12,
  color: '#666',
};

const printBtnStyle: React.CSSProperties = {
  border: '1px solid #111',
  background: '#111',
  color: 'white',
  padding: '6px 14px',
  borderRadius: 4,
  fontSize: 12,
  cursor: 'pointer',
};

const blockStyle: React.CSSProperties = {
  marginBottom: 24,
  pageBreakInside: 'avoid',
};

const blockKickerStyle: React.CSSProperties = {
  fontFamily: 'monospace',
  fontSize: 10,
  textTransform: 'uppercase',
  letterSpacing: '0.18em',
  color: '#999',
  marginBottom: 4,
};

const h2Style: React.CSSProperties = {
  margin: '0 0 8px',
  fontSize: 18,
  fontWeight: 600,
};

const pitchStyle: React.CSSProperties = {
  fontStyle: 'italic',
  borderLeft: '2px solid #111',
  paddingLeft: 12,
  margin: '8px 0',
  fontSize: 13,
};

const pillRowStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
  alignItems: 'center',
  margin: '6px 0',
};

const pillLabelStyle: React.CSSProperties = {
  fontFamily: 'monospace',
  fontSize: 9,
  textTransform: 'uppercase',
  letterSpacing: '0.18em',
  color: '#666',
};

const pillStyle: React.CSSProperties = {
  fontSize: 11,
  background: '#f1f1f1',
  padding: '2px 8px',
  borderRadius: 999,
  color: '#222',
};

const feelStyle: React.CSSProperties = {
  fontFamily: 'monospace',
  fontSize: 10,
  textTransform: 'uppercase',
  letterSpacing: '0.18em',
  color: '#666',
  marginTop: 6,
};

const olStyle: React.CSSProperties = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
};

const sectionItemStyle: React.CSSProperties = {
  display: 'flex',
  gap: 10,
  fontSize: 12,
  margin: '4px 0',
};

const olMarkerStyle: React.CSSProperties = {
  fontFamily: 'monospace',
  color: '#999',
};

const colorChipRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 4,
};

const footerStyle: React.CSSProperties = {
  marginTop: 32,
  paddingTop: 12,
  borderTop: '1px solid #ddd',
  fontSize: 10,
  color: '#888',
};
